import {ChangeDetectionStrategy, Component, inject, input, output, viewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormService, SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeModalComponent, SeFeModalModule} from 'se-fe-modal';
import {Catalog} from '../../../models/clare';
import {trimFormValue} from '../../../utils/form-utils';

export interface CatalogFormValue {
  name: string;
  description: string;
}

interface CatalogForm {
  name: FormControl<string>;
  description: FormControl<string>;
}

const EMPTY_CATALOG_FORM_VALUE: Pick<Catalog, 'name' | 'description'> = {
  name: '',
  description: '',
};

@Component({
  selector: 'app-catalog-form-modal',
  templateUrl: './catalog-form-modal.component.html',
  styleUrl: './catalog-form-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SeFeAlertModule,
    SeFeButtonModule,
    SeFeFormFieldModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeModalModule,
  ],
})
export class CatalogFormModalComponent {
  modalTitle = input.required<string>();
  savingLabel = input.required<string>();
  initialValue = input<Pick<Catalog, 'name' | 'description'>>(EMPTY_CATALOG_FORM_VALUE);
  saving = input(false);
  errorMessage = input<string>();
  save = output<CatalogFormValue>();

  readonly modal = viewChild.required<SeFeModalComponent>('modal');

  private readonly fb = inject(FormBuilder);
  private readonly formService = inject(SeFeFormService);

  readonly catalogForm: FormGroup<CatalogForm> = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    description: ['', [Validators.maxLength(500)]],
  });

  readonly catalogNameConfig: SeFeFormFieldConfig = {
    label: 'Catalog Name',
    required: true,
    errorMessages: {
      required: 'Please enter a catalog name',
      maxlength: 'Name exceeds maximum length of 100 characters.',
    },
  };

  readonly catalogDescriptionConfig: SeFeFormFieldConfig = {
    label: 'Catalog Description',
    required: false,
    errorMessages: {
      maxlength: 'Description exceeds maximum length of 500 characters.',
    },
  };

  open(trigger: unknown): void {
    this.resetForm();
    this.modal().open(trigger);
  }

  close(): void {
    this.modal().close();
  }

  submit(): void {
    trimFormValue(this.catalogForm, 'name');
    trimFormValue(this.catalogForm, 'description');

    if (!this.formService.validate(this.catalogForm)) {
      return;
    }

    this.save.emit(this.catalogForm.getRawValue());
  }

  resetForm(): void {
    const initialValue = this.initialValue();
    this.catalogForm.reset({
      name: initialValue.name,
      description: initialValue.description ?? '',
    });
    this.catalogForm.markAsPristine();
    this.catalogForm.markAsUntouched();
  }
}
